import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography, useTheme } from '@mui/joy';
import { useMediaQuery } from '@mui/material';

import DeviceSpeed from '../../assets/images/DeviceSpeed.svg?react';
import Home from '../../assets/images/Home.svg?react';
import PaperPlane from '../../assets/images/PaperPlane.svg?react';
import RemainingTime from '../../assets/images/RemainingTime.svg?react';
import Time from '../../assets/images/Time.svg?react';

type DockProps = {
  agl: string;
  asl: string;
  airSpeed: string;
  groundSpeed: string;
  time: string;
  remainingTime: string;
  homeDistance: string;
};

const Dock = React.memo(({ agl, asl, airSpeed, groundSpeed, time, remainingTime, homeDistance }: DockProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileLandscape = useMediaQuery('(orientation: landscape) and (max-width: 1024px)');

  const labelStyle = {
    color: 'text.primary',
    fontSize: '0.66rem',
    fontWeight: 400,
    lineSpacing: '0%'
  };

  const valueStyle = {
    color: 'text.primary',
    fontSize: '1.257rem',
    fontWeight: 400
  };

  return (
    <Box
      className='flex flex-row flex-wrap sm:flex-nowrap justify-center items-center gap-1 sm:gap-1 h-auto sm:h-10 p-1.5 sm:p-0 sm:rounded-sm'
      sx={{
        bgcolor: 'background.level1',
        borderRadius: '4px',
        // Mobile landscape: scale down for wider screens (>=910px), full width for smaller
        '@media (orientation: landscape) and (min-width: 910px) and (max-width: 1024px)': {
          transform: 'scale(0.7)',
          transformOrigin: 'center center',
          width: 'fit-content',
          margin: '0 auto'
        },
        '@media (orientation: landscape) and (max-width: 909px)': {
          width: '100%',
          padding: '4px 8px',
          borderRadius: '0px' // Remove border radius for full width
        }
      }}
    >
      <Box
        className='flex flex-wrap sm:flex-nowrap flex-row gap-1.5 sm:gap-2 p-0 sm:p-1 justify-center sm:justify-start h-auto sm:h-[30px] sm:rounded-sm'
        sx={{
          bgcolor: 'background.level1'
        }}
      >
        {/* AGL */}
        <Box className='flex flex-row justify-center items-center h-auto sm:h-full gap-0.5 sm:gap-0'>
          <Box className='flex w-6 h-5 items-center justify-center shrink-0'>
            <Typography level='title-sm' sx={labelStyle}>
              {t('crew_view.dock.agl')}
            </Typography>
          </Box>
          <Typography level='body-md' sx={valueStyle}>
            {agl} {t('crew_view.units.feet')}
          </Typography>
        </Box>

        {/* ASL */}
        <Box className='flex flex-row justify-center items-center h-auto sm:h-full gap-0.5 sm:gap-0'>
          <Box className='flex w-6 h-5 items-center justify-center shrink-0'>
            <Typography level='title-sm' sx={labelStyle}>
              {t('crew_view.dock.asl')}
            </Typography>
          </Box>
          <Typography level='body-md' sx={valueStyle}>
            {asl} {t('crew_view.units.feet')}
          </Typography>
        </Box>

        {/* Air Speed */}
        <Box className='flex flex-row justify-center items-center h-auto sm:h-full'>
          <Box className='flex w-6 h-6 items-center justify-center shrink-0'>
            <PaperPlane className='w-4 h-4 sm:w-[13px] sm:h-[14px]' fill={theme.palette.crew.iconLightGray} />
          </Box>
          <Typography level='body-md' sx={valueStyle}>
            {airSpeed} {t('crew_view.units.mph')}
          </Typography>
        </Box>

        {/* Ground Speed */}
        <Box className='flex flex-row justify-center items-center h-auto sm:h-full'>
          <Box className='flex w-6 h-6 items-center justify-center shrink-0'>
            <DeviceSpeed className='w-4 h-4 sm:w-[13px] sm:h-[14px]' fill={theme.palette.crew.iconLightGray} />
          </Box>
          <Typography level='body-md' sx={valueStyle}>
            {groundSpeed} {t('crew_view.units.mph')}
          </Typography>
        </Box>

        {/* Time */}
        <Box className='flex flex-row justify-center items-center h-auto sm:h-full'>
          <Box className='flex w-6 h-6 items-center justify-center shrink-0'>
            <Time className='w-4 h-4 sm:w-[13px] sm:h-[14px]' fill={theme.palette.crew.iconLightGray} />
          </Box>
          <Typography level='body-md' sx={valueStyle}>
            {time}
          </Typography>
        </Box>

        {/* Home Distance */}
        <Box className='flex flex-row justify-center items-center h-auto sm:h-full'>
          <Box className='flex w-4 h-4 items-center justify-center shrink-0'>
            <Home className='w-4 h-4 sm:w-[13px] sm:h-[14px]' fill={theme.palette.crew.iconLightGray} />
          </Box>
          <Box>
            <Typography level='body-md' sx={valueStyle}>
              {homeDistance} {t('crew_view.units.feet')}
            </Typography>
          </Box>
        </Box>

        {/* Remaining Time */}
        <Box className='flex flex-row justify-center items-center h-auto sm:h-full'>
          <Box className='flex w-4 h-4 items-center justify-center shrink-0'>
            <RemainingTime className='w-4 h-4 sm:w-[13px] sm:h-[14px]' fill={theme.palette.crew.iconLightGray} />
          </Box>
          <Typography level='body-md' sx={valueStyle}>
            {remainingTime}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

Dock.displayName = 'Dock';

export default Dock;
